import React, { useEffect, useState } from 'react';
import ClockTimeField from '../components/Common/ClockTimeField';
import { formatPreferredDate, shootTimeHasPassed, studioTodayParts } from '../utils/preferredDate';
import { PlaceSearch, ShootMap, EMPTY_SHOOT_LOCATION, hasShootLocation, isGoogleMapsLink, normalizeShootLocation, shootMapLink, ShootLocation } from '../components/Common/ShootLocationField';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiBase';
import { downloadResponseFile } from '../utils/downloadFile';
import { paymentAwaitingVerification, paymentReturnMessage } from '../utils/paymentReturn';
import ClientCorrespondence, { useClientPortal } from '../components/Quote/ClientCorrespondence';
import MoneySummary from '../components/Quote/MoneySummary';
import { formatPackageDuration, quotePackageDuration } from '../utils/packageDuration';
import { formatQuoteMoney, PricedLine, QuotePricingSnapshot } from '../utils/quotePricing';
import { accountStatusSentence, paymentRowLabel } from '../utils/moneyPresentation';
import { LetterFlow } from '../components/Quote/LetterText';
import { parseQuoteLetter } from '../utils/quoteLetter';
import { clientFacingShootType } from '../utils/quoteRecord';
import { recordEvent } from '../utils/analytics';
import './ClientQuotePage.css';

function QuoteLetter({ text }: { text: string }) {
  const pages = parseQuoteLetter(text);
  if (!pages.length) return null;
  return (
    <div className="quote-letter">
      {pages.map((page, pageIndex) => (
        <div className="quote-letter-page" key={pageIndex}>
          {page.map((paragraph, paragraphIndex) => (
            <LetterFlow lines={paragraph} paragraphClassName="quote-message" key={paragraphIndex} />
          ))}
        </div>
      ))}
    </div>
  );
}

function foreignNote(snapshot: QuotePricingSnapshot, rateText: string, lockedMinor: number, booking = false): string {
  const priced = booking
    ? `This booking is priced in ${snapshot.currency}.`
    : `This quotation is priced in ${snapshot.currency}.`;
  const card = ' PayFast charges the rand amount shown. Your card issuer may use a different exchange rate and may add its own charges.';
    if (snapshot.settlementPolicy === 'market-v2') {
    const rate = rateText ? ` The recorded market rate is ${rateText}.` : '';
    return `${priced} The rand amount of ${formatQuoteMoney(lockedMinor, 'R')} is the final ${snapshot.currency} price converted at the market rate.${rate}${card}`;
  }
  const rate = rateText
    ? ` The market rate recorded with the ${booking ? 'booking' : 'quotation'} is ${rateText}.`
    : '';
  return `${priced} The agreed rand amount shown is the amount that will be charged.${rate}${card}`;
}

type ClientTab = 'quote' | 'shoot' | 'messages';
const CLIENT_TABS: ClientTab[] = ['quote', 'shoot', 'messages'];

interface ShootDraftSession {
  id: string;
  date: string;
  time: string;
  location: ShootLocation;
}

interface ShootDraft {
  date: string;
  time: string;
  location: ShootLocation;
  sessions?: ShootDraftSession[];
}

interface ChosenSession {
  id: string;
  label: string;
  date: string;
  startTime: string;
  durationMinutes?: number;
  location: ShootLocation;
}

const draftKey = (token?: string) => `quote-shoot-draft:${token || ''}`;

function readShootDraft(token?: string): ShootDraft | null {
  try {
    const saved = JSON.parse(window.localStorage.getItem(draftKey(token)) || 'null');
    if (!saved || typeof saved !== 'object') return null;
    const sessions = Array.isArray(saved.sessions)
      ? saved.sessions.filter((item: { id?: unknown }) => item && typeof item.id === 'string').map((item: { id: string; date?: string; time?: string; location?: Partial<ShootLocation> }) => ({
        id: item.id,
        date: typeof item.date === 'string' ? item.date : '',
        time: typeof item.time === 'string' ? item.time : '',
        location: normalizeShootLocation(item.location),
      }))
      : undefined;
    return {
      date: typeof saved.date === 'string' ? saved.date : '',
      time: typeof saved.time === 'string' ? saved.time : '',
      location: normalizeShootLocation(saved.location),
      ...(sessions && sessions.length ? { sessions } : {}),
    };
  } catch {
    return null;
  }
}

function writeShootDraft(token: string | undefined, draft: ShootDraft) {
  try {
    window.localStorage.setItem(draftKey(token), JSON.stringify(draft));
  } catch {
    // Private browsing can refuse storage; the choice still holds for this visit.
  }
}

function clearShootDraft(token?: string) {
  try {
    window.localStorage.removeItem(draftKey(token));
  } catch {
    // Nothing to clear.
  }
}

type ShootRow = 'date' | 'time' | 'place';

/**
 * The client's date, time and place as plain rows. Each row has its own Change button,
 * and only the row being changed opens its field.
 */
function ShootChoices({
  date,
  time,
  location,
  dateInPast,
  timeHasPassed,
  onDate,
  onTime,
  onLocation,
  subject = '',
}: {
  date: string;
  time: string;
  location: ShootLocation;
  dateInPast: boolean;
  timeHasPassed: boolean;
  onDate: (value: string) => void;
  onTime: (value: string) => void;
  onLocation: (value: ShootLocation) => void;
  /** Day name, so each day's fields stay distinct when several are on the page. */
  subject?: string;
}) {
  const [editing, setEditing] = useState<ShootRow | null>(null);
  const [typing, setTyping] = useState(false);
  const hasPlace = hasShootLocation(location);
  const placeMap = hasPlace ? shootMapLink(location) : '';

  const open = (row: ShootRow) => {
    setTyping(false);
    setEditing(row);
  };
  const done = () => {
    setTyping(false);
    setEditing(null);
  };

  const rowName = (row: ShootRow) => (subject ? `${subject} ${row}` : row);
  const rowActions = (row: ShootRow, filled: boolean, clear: () => void) => (
    <div className="shoot-row-actions">
      <button type="button" className="shoot-row-change" onClick={() => open(row)} aria-label={`${filled ? 'Change' : 'Choose'} ${rowName(row)}`}>
        {filled ? 'Change' : 'Choose'}
      </button>
      {filled ? (
        <button type="button" className="shoot-row-clear" onClick={clear} aria-label={`Remove ${row}`}>Remove</button>
      ) : null}
    </div>
  );

  return (
    <div className="shoot-choices">
      <div className={`shoot-row${editing === 'date' ? ' is-editing' : ''}`}>
        <span className="shoot-row-label">Date</span>
        {editing === 'date' ? (
          <div className="shoot-row-editor">
            <input
              type="date"
              aria-label={subject ? `${subject} date` : 'Shoot date'}
              min={studioTodayParts().date}
              value={date}
              autoFocus
              onChange={(event) => onDate(event.target.value)}
            />
            <button type="button" className="shoot-row-done" onClick={done}>Done</button>
          </div>
        ) : (
          <>
            <span className="shoot-row-value">{date ? formatPreferredDate(date) : <em>Not chosen. Carmen will agree it with you.</em>}</span>
            {rowActions('date', Boolean(date), () => onDate(''))}
          </>
        )}
        {dateInPast ? <p className="shoot-row-error" role="alert">Please choose a date that has not passed.</p> : null}
      </div>

      <div className={`shoot-row${editing === 'time' ? ' is-editing' : ''}`}>
        <span className="shoot-row-label">Start time</span>
        {editing === 'time' ? (
          <div className="shoot-row-editor">
            <ClockTimeField className="clock-time-quote" ariaLabel={subject ? `${subject} start time` : 'Start time'} value={time} onChange={onTime} autoOpen />
            <button type="button" className="shoot-row-done" onClick={done}>Done</button>
          </div>
        ) : (
          <>
            <span className="shoot-row-value">{time || <em>No preference</em>}</span>
            {rowActions('time', Boolean(time), () => onTime(''))}
          </>
        )}
        {timeHasPassed ? <p className="shoot-row-error" role="alert">That time today has already passed. Choose a later time.</p> : null}
      </div>

      <div className={`shoot-row${editing === 'place' ? ' is-editing' : ''}`}>
        <span className="shoot-row-label">Place</span>
        {editing === 'place' ? (
          <div className="shoot-row-editor is-place">
            {typing ? (
              <>
                <label>
                  <span>Place name</span>
                  <input
                    type="text"
                    maxLength={200}
                    value={location.name}
                    autoFocus
                    onChange={(event) => onLocation({ ...location, name: event.target.value, mapsUrl: '' })}
                    placeholder="Kirstenbosch Gardens"
                  />
                </label>
                <label>
                  <span>Address</span>
                  <input
                    type="text"
                    maxLength={400}
                    value={location.address}
                    onChange={(event) => onLocation({ ...location, address: event.target.value, mapsUrl: '' })}
                    placeholder="Rhodes Drive, Newlands, Cape Town"
                  />
                </label>
                <div className="shoot-row-editor-actions">
                  <button type="button" className="shoot-row-done" onClick={done}>Done</button>
                  <button type="button" className="quote-text-button" onClick={() => setTyping(false)}>Search instead</button>
                </div>
              </>
            ) : (
              <>
                <PlaceSearch onSelect={(place) => { onLocation(place); done(); }} />
                <div className="shoot-row-editor-actions">
                  <button type="button" className="quote-text-button" onClick={() => setTyping(true)}>Can&rsquo;t find it? Type the address</button>
                  <button type="button" className="quote-text-button" onClick={done}>Cancel</button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <span className="shoot-row-value">
              {hasPlace ? (
                <>
                  <strong>{location.name || location.address}</strong>
                  {location.name && location.address ? <small>{location.address}</small> : null}
                  {placeMap ? <a href={placeMap} target="_blank" rel="noreferrer">Open in Google Maps</a> : null}
                </>
              ) : <em>Not chosen. Carmen will agree it with you.</em>}
            </span>
            {rowActions('place', hasPlace, () => onLocation({ ...EMPTY_SHOOT_LOCATION }))}
          </>
        )}
      </div>
      {editing !== 'place' && hasPlace ? <ShootMap mapsUrl={location.mapsUrl} label={location.name || location.address} /> : null}
    </div>
  );
}

interface MoneyFigures {
  totalMinor: number;
  depositRequiredMinor: number;
  receivedMinor: number;
  outstandingMinor: number;
  currentlyDueMinor: number;
  futureBalanceMinor: number;
  secured: boolean;
  paidInFull: boolean;
  overdueMinor?: number;
  requestDueOn?: string;
}

interface PublicQuote {
  quoteNumber: string;
  version: number;
  status: 'issued' | 'accepted' | 'declined' | 'superseded' | 'expired' | 'cancelled';
  clientName: string;
  personalMessage: string;
  shootType: string;
  preferredDate?: string;
  preferredTime?: string;
  preferredLocation?: Partial<ShootLocation>;
  sessions?: Array<{
    id?: string;
    label?: string;
    date?: string;
    startTime?: string;
    durationMinutes?: number;
    location?: Partial<ShootLocation> | null;
  }>;
  adhocPackage?: { name?: string; description?: string; features?: string[] } | null;
  guides?: Array<{ id: string; title: string }>;
  snapshot: QuotePricingSnapshot;
  issuedAt?: string | null;
  expiresAt?: string | null;
  validDays?: number;
  exchangeRate?: { fromCurrency: string; toCurrency: string; exchangeRate: number } | null;
  figures?: MoneyFigures | null;
  shootConfirmed?: boolean;
}

interface PaymentAction {
  type: 'deposit' | 'balance' | 'full' | 'amount';
  amountMinor: number;
  minimumMinor?: number;
}

interface PaymentRecord {
  type: string;
  status: string;
  expectedMinor: number;
  verifiedMinor: number;
  reference?: string | null;
  paidAt?: string | null;
}

interface PaymentHandoff {
  ready: boolean;
  depositMinor: number;
  depositDueMinor: number;
  totalMinor: number;
  receivedMinor: number;
  outstandingMinor: number;
  minimumMinor: number;
  maximumMinor: number;
  secured: boolean;
  quoteCurrency: string;
  quoteTotalMinor: number;
  arrangement: { status: string; amountMinor: number; scheduleMinor: number[]; note: string } | null;
  request: { status: string; amountMinor: number; note: string } | null;
  actions: PaymentAction[];
  payments: PaymentRecord[];
  figures?: MoneyFigures;
}

function studioDate(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Africa/Johannesburg',
  }).format(date);
}

function randsToMinor(value: string): number | null {
  const trimmed = value.trim().replace(/[R\s,]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole, fraction = ''] = trimmed.split('.');
  return (Number(whole) * 100) + Number((fraction + '00').slice(0, 2));
}

function minorToInput(minor: number): string {
  return (minor / 100).toFixed(2);
}

function sessionWhen(date?: string, time?: string): string {
  if (!date && !time) return '';
  return [date ? (formatPreferredDate(date) || date) : '', time].filter(Boolean).join(' at ');
}

function lineDetails(line: PricedLine): string {
  const phrase = (line.kind === 'package' || line.kind === 'session')
    ? formatPackageDuration(line.durationMinutes)
    : '';
  const coverage = phrase ? `Duration: ${phrase}` : '';
  if (line.complimentary) return [coverage, 'Included'].filter(Boolean).join(' · ');
  if (line.pricingType === 'per-hour') {
    const hours = `${line.units} hour${line.units === 1 ? '' : 's'}`;
    return [coverage, hours].filter(Boolean).join(' · ');
  }
  if (line.pricingType === 'per-each' && line.units > 1) {
    return [coverage, String(line.units)].filter(Boolean).join(' · ');
  }
  return coverage;
}

function toChosenSession(session: {
  id?: string;
  label?: string;
  date?: string;
  startTime?: string;
  durationMinutes?: number;
  location?: Partial<ShootLocation> | null;
}, index: number): ChosenSession {
  return {
    id: session.id || `session-${index + 1}`,
    label: session.label || `Day ${index + 1}`,
    date: session.date || '',
    startTime: session.startTime || '',
    durationMinutes: session.durationMinutes,
    location: normalizeShootLocation(session.location),
  };
}

function PaymentChoices({
  details,
  depositPercent,
  otherAmount,
  busy,
  onOtherAmount,
  onPay,
}: {
  details: PaymentHandoff;
  depositPercent?: number;
  otherAmount: string;
  busy: boolean;
  onOtherAmount: (value: string) => void;
  onPay: (type: PaymentAction['type'], amountMinor?: number) => void;
}) {
  const minimum = details.minimumMinor;
  const maximum = details.maximumMinor;
  const depositDue = details.depositDueMinor;
  const dueNow = details.figures?.currentlyDueMinor ?? (depositDue > 0 ? depositDue : maximum);
  const primaryMinor = dueNow > 0 && dueNow >= minimum && dueNow <= maximum ? dueNow : maximum;
  let primaryType: PaymentAction['type'] = 'amount';
  if (depositDue > 0 && primaryMinor === depositDue) primaryType = 'deposit';
  else if (primaryMinor === maximum && depositDue === 0) primaryType = 'balance';
  else if (primaryMinor === maximum) primaryType = 'full';
  const canFull = maximum > primaryMinor;
  const canOther = maximum > primaryMinor && maximum > minimum;
  const typed = randsToMinor(otherAmount);
  const otherPayable = typed != null && typed >= minimum && typed <= maximum;
  const approvedLower = minimum > 0 && depositDue > minimum && primaryMinor === minimum;

  return (
    <section className="quote-choice" aria-label="Payment">
      {approvedLower ? (
        <p className="quote-choice-lead">A first payment of {formatQuoteMoney(minimum, 'R')} has been approved.</p>
      ) : null}
      <div className="quote-actions">
        <button
          type="button"
          className="quote-accept"
          disabled={busy || primaryMinor < 1}
          onClick={() => onPay(primaryType, primaryType === 'amount' ? primaryMinor : undefined)}
        >
          {primaryType === 'deposit'
            ? `Pay deposit${depositPercent ? ` (${depositPercent}%)` : ''} ${formatQuoteMoney(primaryMinor, 'R')}`
            : `Pay ${formatQuoteMoney(primaryMinor, 'R')}`}
        </button>
        {canFull ? (
          <button
            type="button"
            className="quote-decline quote-pay-full"
            disabled={busy}
            onClick={() => onPay('full')}
          >
            Pay in full {formatQuoteMoney(maximum, 'R')}
          </button>
        ) : null}
      </div>
      {canOther ? (
        <details className="quote-choice-other">
          <summary>Pay a different amount</summary>
          <label className="quote-choice-custom">
            <span>Between {formatQuoteMoney(minimum, 'R')} and {formatQuoteMoney(maximum, 'R')}</span>
            <input
              inputMode="decimal"
              value={otherAmount}
              placeholder={minorToInput(minimum)}
              onChange={(event) => onOtherAmount(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="quote-decline"
            disabled={busy || !otherPayable}
            onClick={() => onPay('amount', typed || undefined)}
          >
            {otherPayable && typed != null ? `Pay ${formatQuoteMoney(typed, 'R')}` : 'Enter an amount'}
          </button>
        </details>
      ) : null}
    </section>
  );
}

async function downloadQuotationPdf(quoteToken: string, report: (message: string) => void) {
  report('');
  try {
    const response = await fetch(`${API_BASE_URL}/public/quotes/${quoteToken}/document.pdf`, {
      headers: { Accept: 'application/pdf' },
    });
    if (!response.ok) {
      report('The PDF could not be prepared.');
      return;
    }
    await downloadResponseFile(response, 'Carmen-Lorraine-quotation.pdf');
  } catch {
    report('The PDF could not be prepared.');
  }
}

const ClientQuotePage: React.FC<{ payment?: boolean }> = ({ payment = false }) => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentHandoff | null>(null);
  const [error, setError] = useState('');
  const {
    portal,
    loaded: portalLoaded,
    loadError: portalError,
    reload: reloadPortal,
    spokenCount,
    studioWaiting,
    shootConfirmed,
  } = useClientPortal(token);
  // The opening tab is chosen once, so the page does not jump while the client reads or types.
  const [activeTab, setActiveTab] = useState<ClientTab | null>(null);
  const [busy, setBusy] = useState(false);
  const [pdfNote, setPdfNote] = useState('');
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [otherAmount, setOtherAmount] = useState('');
  // Starts as the date Carmen entered (if any); the client can change or clear it before accepting.
  const [chosenDate, setChosenDate] = useState('');
  const [chosenTime, setChosenTime] = useState('');
  const [chosenLocation, setChosenLocation] = useState<ShootLocation>({ ...EMPTY_SHOOT_LOCATION });
  const [chosenSessions, setChosenSessions] = useState<ChosenSession[]>([]);
  const [pendingScroll, setPendingScroll] = useState('');

  useEffect(() => {
    let active = true;
    setError('');
    fetch(`${API_BASE_URL}/public/quotes/${token}`)
      .then(async (response) => {
        if (!response.ok) throw new Error('This page could not be found.');
        const body = await response.json();
        if (active) {
          setQuote(body);
          const draft = body.status === 'issued' ? readShootDraft(token) : null;
          setChosenDate(draft ? draft.date : (body.preferredDate || ''));
          setChosenTime(draft ? draft.time : (body.preferredTime || ''));
          setChosenLocation(normalizeShootLocation(draft ? draft.location : body.preferredLocation));
          const loadedSessions: NonNullable<PublicQuote['sessions']> = Array.isArray(body.sessions) ? body.sessions : [];
          setChosenSessions(loadedSessions.length > 1
            ? loadedSessions.map((session, index) => {
              const chosen = toChosenSession(session, index);
              const saved = draft?.sessions?.find((item) => item.id === chosen.id);
              return saved
                ? { ...chosen, date: saved.date, startTime: saved.time, location: saved.location }
                : chosen;
            })
            : []);
        }
      })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'This page could not be found.');
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if ((!payment && quote?.status !== 'accepted') || !token) return undefined;
    let active = true;
    let polls = 0;
    let timer = 0;
    const load = () => {
      fetch(`${API_BASE_URL}/public/quotes/${token}/payment`)
        .then(async (response) => {
          const body = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(body.error || 'Payment is not available.');
          if (active) setPaymentDetails(body);
          if (body && !paymentAwaitingVerification(body) && body.receivedMinor > 0) window.clearInterval(timer);
        })
        .catch((loadError) => {
          if (active) setError(loadError instanceof Error ? loadError.message : 'Payment is not available.');
        });
    };
    load();
    if (searchParams.get('result') !== 'returned') {
      return () => {
        active = false;
      };
    }
    timer = window.setInterval(() => {
      polls += 1;
      if (polls > 30) {
        window.clearInterval(timer);
        return;
      }
      load();
    }, 2000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [payment, token, quote?.status, searchParams]);

  useEffect(() => {
    if (!quote && !portalLoaded) return undefined;
    const booking = shootConfirmed || Boolean(quote?.shootConfirmed);
    document.title = `${booking ? 'Your booking' : 'Your quotation'} - Carmen Lorraine Photography`;
    return undefined;
  }, [quote, portalLoaded, shootConfirmed]);

  useEffect(() => {
    if (quote?.status === 'issued') {
      writeShootDraft(token, {
        date: chosenDate,
        time: chosenTime,
        location: chosenLocation,
        ...(chosenSessions.length > 1
          ? {
            sessions: chosenSessions.map((session) => ({
              id: session.id,
              date: session.date,
              time: session.startTime,
              location: session.location,
            })),
          }
          : {}),
      });
    }
  }, [quote?.status, token, chosenDate, chosenTime, chosenLocation, chosenSessions]);

  const paymentsReady = Boolean(paymentDetails) || Boolean(error);
  useEffect(() => {
    if (activeTab !== null || !quote || !portalLoaded) return;
    if (quote.status === 'accepted' && !paymentsReady) return;
    const linked = window.location.hash.replace('#', '');
    if (CLIENT_TABS.includes(linked as ClientTab)) {
      setActiveTab(linked as ClientTab);
      return;
    }
    const owes = quote.status === 'accepted' && (paymentDetails?.outstandingMinor ?? 0) > 0;
    if (searchParams.get('result') || quote.status === 'issued' || owes) setActiveTab('quote');
    else if (studioWaiting) setActiveTab('messages');
    else setActiveTab('shoot');
  }, [activeTab, quote, portalLoaded, paymentsReady, paymentDetails, studioWaiting, searchParams]);

  const goTo = (next: ClientTab, target?: string) => {
    setActiveTab(next);
    setPendingScroll(target || 'quote-tabs-anchor');
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${next}`);
  };

  useEffect(() => {
    if (!pendingScroll || activeTab === null) return undefined;
    const handle = window.requestAnimationFrame(() => {
      document.getElementById(pendingScroll)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(handle);
  }, [pendingScroll, activeTab]);

  const decide = async (decision: 'accept' | 'decline') => {
    setBusy(true);
    setError('');
    try {
      const acceptSchedule = chosenSessions.length > 1
        ? {
          preferredDate: chosenSessions[0].date,
          preferredTime: chosenSessions[0].startTime,
          preferredLocation: chosenSessions[0].location,
          sessions: chosenSessions.map((session) => ({
            id: session.id,
            date: session.date,
            startTime: session.startTime,
            location: session.location,
          })),
        }
        : { preferredDate: chosenDate, preferredTime: chosenTime, preferredLocation: chosenLocation };
      const response = await fetch(`${API_BASE_URL}/public/quotes/${token}/${decision}`, decision === 'accept'
        ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(acceptSchedule),
        }
        : { method: 'POST' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The quotation could not be updated.');
      if (decision === 'accept') recordEvent('generate_lead', { method: 'quote_accept' });
      setQuote(body);
      clearShootDraft(token);
      if (decision === 'accept') {
        const refreshed = await fetch(`${API_BASE_URL}/public/quotes/${token}`);
        if (refreshed.ok) setQuote(await refreshed.json());
      }
      goTo('quote');
    } catch (decideError) {
      setError(decideError instanceof Error ? decideError.message : 'The quotation could not be updated.');
    } finally {
      setBusy(false);
    }
  };

  const startPayment = async (type: PaymentAction['type'], amountMinor?: number) => {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/public/quotes/${token}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(type === 'amount' ? { type, amountMinor } : { type }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Payment could not be started.');
      if (!body.ready || !body.url || !body.fields) {
        throw new Error('PayFast is not connected yet.');
      }
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = body.url;
      Object.entries(body.fields as Record<string, string>).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : 'Payment could not be started.');
      setBusy(false);
    }
  };

  if (error && !quote) {
    return <main className="client-quote"><p className="client-quote-status">{error}</p></main>;
  }
  if (!quote) {
    const opening = !portalLoaded
      ? 'Opening…'
      : shootConfirmed
        ? 'Opening your booking…'
        : 'Opening your quotation…';
    return <main className="client-quote"><p className="client-quote-status">{opening}</p></main>;
  }

  const bookingOpen = shootConfirmed || Boolean(quote.shootConfirmed);
  const snapshot = quote.snapshot;
  const expired = quote.status === 'expired' || (quote.status === 'issued' && Boolean(quote.expiresAt) && new Date(quote.expiresAt || '').getTime() <= Date.now());
  const deciding = quote.status === 'issued' && !expired;
  const shownDate = deciding ? chosenDate : (quote.preferredDate || '');
  const dateInPast = Boolean(chosenDate) && chosenDate < studioTodayParts().date;
  const timeHasPassed = shootTimeHasPassed(chosenDate, chosenTime);
  const badMapsLink = !isGoogleMapsLink(chosenLocation.mapsUrl || '');
  const shownTime = deciding ? chosenTime : (quote.preferredTime || '');
  const shownLocation = deciding ? chosenLocation : normalizeShootLocation(quote.preferredLocation);
  const shownPlace = [shownLocation.name, shownLocation.address].filter(Boolean).join(', ');
  const shownMap = hasShootLocation(shownLocation) ? shootMapLink(shownLocation) : '';
  const issuedLabel = studioDate(quote.issuedAt);
  const validUntil = studioDate(quote.expiresAt);
  const rateText = quote.exchangeRate
    ? `1 ZAR = ${quote.exchangeRate.exchangeRate} ${quote.exchangeRate.toCurrency}`
    : '';
  const packages = snapshot.lines.filter((line) => line.kind === 'package' || line.kind === 'session');
  const included = snapshot.lines.filter((line) => line.kind !== 'package' && line.kind !== 'session' && line.complimentary);
  const extras = snapshot.lines.filter((line) => line.kind === 'extra' && !line.complimentary);
  const custom = snapshot.lines.filter((line) => line.kind === 'custom' && !line.complimentary);
  const ungrouped = snapshot.lines.filter((line) => !line.kind);
  const quoteSessions = Array.isArray(quote.sessions) ? quote.sessions.filter(Boolean) : [];
  const multiDay = quoteSessions.length > 1;
  const storedSessions = quoteSessions.map((session, index) => toChosenSession(session, index));
  const daySessions = deciding && chosenSessions.length > 1 ? chosenSessions : storedSessions;
  const closed = expired || quote.status === 'declined' || quote.status === 'superseded' || quote.status === 'cancelled';
  const depositMinor = paymentDetails?.depositMinor ?? snapshot.depositMinor;
  const lockedZarMinor = paymentDetails?.totalMinor ?? snapshot.zarTotalMinor;

  const masthead = (
    <header className={`quote-masthead${quote.status === 'accepted' ? ' is-compact' : ''}`}>
      <img className="quote-mark" src="/brand/carmen-lorraine-mark.svg" alt="" />
      <p className="quote-studio">Carmen Lorraine Photography</p>
      <p className="quote-reference">
        <span>{quote.quoteNumber}</span>
        {quote.version > 1 ? <span>Version {quote.version}</span> : null}
        {issuedLabel ? <span>Issued {issuedLabel}</span> : null}
      </p>
      {token ? (
        <p className="quote-download">
          <button type="button" onClick={() => downloadQuotationPdf(token, setPdfNote)}>Download PDF</button>
          {pdfNote ? <span className="quote-download-note">{pdfNote}</span> : null}
        </p>
      ) : null}
    </header>
  );

  const figures = paymentDetails?.figures || quote.figures || null;
  const accepted = quote.status === 'accepted';
  const paidInFull = figures?.paidInFull ?? false;
  const secured = figures?.secured ?? paymentDetails?.secured ?? false;
  const owing = accepted && !paidInFull && (paymentDetails?.outstandingMinor ?? 0) > 0;
  const returned = searchParams.get('result');
  const returnMessage = accepted && returned && !paidInFull ? paymentReturnMessage(returned, paymentDetails, bookingOpen) : '';
  const stateLine = !accepted
    ? ''
    : paidInFull
      ? 'Paid in full. Thank you.'
      : secured
        ? 'Deposit received. Your date is held.'
        : bookingOpen
          ? 'Your booking is confirmed. Pay the deposit to secure the date.'
          : 'Quotation accepted. Pay the deposit to secure your date.';
  const moneyProps = {
    currency: snapshot.currency,
    quoteTotalMinor: snapshot.quoteTotalMinor,
    lockedZarMinor,
    depositMinor,
    depositPercent: snapshot.depositPercent,
    figures,
  };
  const money = <MoneySummary mode="proposal" {...moneyProps} />;
  const glance = <MoneySummary mode="glance" {...moneyProps} />;
  const accountSentence = accountStatusSentence(figures);

  const currencyNote = snapshot.currency !== 'R' ? (
    <details className="quote-disclosure">
      <summary>Currency details</summary>
      <p>{foreignNote(snapshot, rateText, lockedZarMinor, bookingOpen)}</p>
    </details>
  ) : null;

  const history = paymentDetails?.payments?.length ? (
    <div className="quote-history" aria-label="Payment history">
      {paymentDetails.payments.map((item, index) => (
        <div className="quote-amount-row" key={`${item.type}-${item.paidAt || index}`}>
          <span>{paymentRowLabel(item.type, item.status)}{item.paidAt ? ` · ${studioDate(item.paidAt)}` : ''}</span>
          <strong>{formatQuoteMoney(item.status === 'complete' ? item.verifiedMinor : item.expectedMinor, 'R')}</strong>
        </div>
      ))}
    </div>
  ) : null;

  const pricedLineCount = snapshot.lines.filter((line) => !line.complimentary).length;
  const showLinePrices = pricedLineCount > 1 || snapshot.quoteDiscountMinor > 0;

  const placeForLine = (line: PricedLine) => {
    if (line.kind !== 'session') return '';
    const match = quoteSessions.find((session) => session.id && line.sessionId && session.id === line.sessionId);
    return [match?.location?.name, match?.location?.address].filter(Boolean).join(', ');
  };

  const updateChosenSession = (id: string, patch: Partial<Pick<ChosenSession, 'date' | 'startTime' | 'location'>>) => {
    setChosenSessions((current) => {
      const source = current.length > 1 ? current : storedSessions;
      return source.map((session) => (session.id === id ? { ...session, ...patch } : session));
    });
  };

  const renderLines = (lines: PricedLine[]) => lines.map((line) => {
    const session = line.kind === 'session'
      ? daySessions.find((item) => item.id && line.sessionId && item.id === line.sessionId)
      : undefined;
    const when = line.kind === 'session'
      ? sessionWhen(session ? session.date : line.date, session ? session.startTime : line.startTime)
      : '';
    const place = session
      ? [session.location.name, session.location.address].filter(Boolean).join(', ')
      : placeForLine(line);
    const details = [lineDetails(line), place].filter(Boolean).join(' · ');
    const dayId = session?.id || line.sessionId || line.id;
    return (
      <div className="quote-line" key={line.id}>
        <div>
          <strong>{line.name}</strong>
          {when || details ? (
            <span className="quote-line-meta">
              {when ? <strong className="quote-line-when">{when}</strong> : null}
              {when && details ? ' · ' : null}
              {details}
            </span>
          ) : null}
          {line.description ? <em className="quote-line-note">{line.description}</em> : null}
          {line.features && line.features.length > 0 ? (
            <ul className="quote-line-features">
              {line.features.map((feature) => <li key={feature}>{feature}</li>)}
            </ul>
          ) : null}
          {line.kind === 'session' && multiDay && !closed ? (
            <button
              type="button"
              className="quote-text-button quote-line-change"
              onClick={() => goTo('shoot', `quote-day-${dayId}`)}
            >
              {deciding ? 'Change date or place' : 'See date and location'}
            </button>
          ) : null}
        </div>
        {line.complimentary
          ? <span className="quote-line-price">Included</span>
          : showLinePrices ? <span className="quote-line-price">{formatQuoteMoney(line.quoteExtendedMinor, snapshot.currency)}</span> : null}
      </div>
    );
  });

  const experience = packages.length + included.length + extras.length + custom.length + ungrouped.length > 0 ? (
    <section className="quote-experience" aria-labelledby="quote-experience-title">
      <h2 id="quote-experience-title">Your photography</h2>
      {packages.length > 0 && (
        <div className="quote-group">
          <h3>Selected package</h3>
          {renderLines(packages)}
        </div>
      )}
      {included.length > 0 && (
        <div className="quote-group">
          <h3>Included</h3>
          {renderLines(included)}
        </div>
      )}
      {extras.length > 0 && (
        <div className="quote-group">
          <h3>Selected extras</h3>
          {renderLines(extras)}
        </div>
      )}
      {custom.length > 0 && (
        <div className="quote-group">
          <h3>Additional services</h3>
          {renderLines(custom)}
        </div>
      )}
      {ungrouped.length > 0 && (
        <div className="quote-group">
          {renderLines(ungrouped)}
        </div>
      )}
    </section>
  ) : null;

  const discountRows = snapshot.quoteDiscountMinor > 0 ? (
    <>
      <div className="quote-amount-row">
        <span>Subtotal</span>
        <strong>{formatQuoteMoney(snapshot.quoteSubtotalMinor, snapshot.currency)}</strong>
      </div>
      <div className="quote-amount-row">
        <span>Discount ({snapshot.discountPercent}%)</span>
        <strong>-{formatQuoteMoney(snapshot.quoteDiscountMinor, snapshot.currency)}</strong>
      </div>
    </>
  ) : null;

  const sessionChoiceInvalid = daySessions.some((session) => (
    (Boolean(session.date) && session.date < studioTodayParts().date)
    || shootTimeHasPassed(session.date, session.startTime)
    || !isGoogleMapsLink(session.location.mapsUrl || '')
  ));
  const shootChoiceInvalid = multiDay ? sessionChoiceInvalid : (dateInPast || timeHasPassed || badMapsLink);

  const investment = (
    <section className="quote-investment" aria-labelledby="quote-investment-title">
      <h2 id="quote-investment-title">Your investment</h2>
      {discountRows}
      {accepted ? (
        <div className="quote-total">
          <span>Price</span>
          <strong>{formatQuoteMoney(snapshot.quoteTotalMinor, snapshot.currency)}</strong>
        </div>
      ) : money}
    </section>
  );

  const terms = (
    <section className="quote-terms" aria-labelledby="quote-terms-title">
      <h2 id="quote-terms-title">Booking terms</h2>
      <p>
        {bookingOpen
          ? 'This is your booking confirmation. The date and place are on Date & location. '
          : validUntil
            ? `This quotation is valid until ${validUntil}. `
            : `This quotation is valid for ${quote.validDays || 30} days. `}
        Prices are not subject to VAT.
      </p>
    </section>
  );

  const confirmedBooking = shootConfirmed && portal?.booking ? portal.booking : null;
  const bookedDate = confirmedBooking?.shootDate || shownDate;
  const bookedTime = confirmedBooking?.startTime || shownTime;
  const bookedEnd = confirmedBooking?.endTime || '';
  const coverage = formatPackageDuration(
    quotePackageDuration(quote)
    || snapshot.lines.find((line) => (line.kind === 'package' || line.kind === 'session') && Number(line.durationMinutes) > 0)?.durationMinutes
  );
  const durationFact = () => (coverage && !multiDay ? (
    <div data-testid="package-duration">
      <dt>Duration</dt>
      <dd>{coverage}</dd>
    </div>
  ) : null);
  const scheduleRows = () => {
    const bookingSessions = confirmedBooking && Array.isArray((confirmedBooking as { sessions?: unknown[] }).sessions)
      ? ((confirmedBooking as { sessions: Array<{ id?: string; label?: string; shootDate?: string; startTime?: string; endTime?: string; durationMinutes?: number; locationName?: string; address?: string }> }).sessions || [])
      : [];
    const rows = bookingSessions.length > 1
      ? bookingSessions.map((session, index) => ({
          id: session.id || `session-${index + 1}`,
          label: session.label || `Day ${index + 1}`,
          value: [
            session.shootDate ? formatPreferredDate(session.shootDate) : 'date to be arranged',
            session.startTime ? (session.endTime ? `${session.startTime} until ${session.endTime}` : session.startTime) : 'time to be agreed',
            formatPackageDuration(session.durationMinutes),
            [session.locationName, session.address].filter(Boolean).join(', '),
          ].filter(Boolean).join(' · '),
        }))
      : multiDay
        ? quoteSessions.map((session, index) => ({
            id: session.id || `session-${index + 1}`,
            label: session.label || `Day ${index + 1}`,
            value: [
              session.date ? formatPreferredDate(session.date) : 'date to be arranged',
              session.startTime || 'time to be agreed',
              formatPackageDuration(session.durationMinutes),
              [session.location?.name, session.location?.address].filter(Boolean).join(', '),
            ].filter(Boolean).join(' · '),
          }))
        : [];
    if (!rows.length) return null;
    return (
      <div data-testid="quote-schedule">
        <dt>{confirmedBooking ? 'Schedule' : 'Preferred schedule'}</dt>
        <dd>
          {rows.map((row) => (
            <span id={`quote-day-${row.id}`} key={row.id} style={{ display: 'block' }}><strong>{row.label}:</strong> {row.value}</span>
          ))}
          {coverage ? <span style={{ display: 'block' }}>Total coverage: {coverage}</span> : null}
        </dd>
      </div>
    );
  };
  const bookedPlace = confirmedBooking
    ? ([confirmedBooking.locationName, confirmedBooking.address].filter(Boolean).join(', ') || shownPlace)
    : shownPlace;
  const bookedMap = confirmedBooking?.mapsUrl || shownMap;
  const chat = token ? (
    <ClientCorrespondence
      token={token}
      quoteNumber={quote.quoteNumber}
      portal={portal}
      studioWaiting={studioWaiting}
      spokenCount={spokenCount}
      loadError={portalError}
      reload={reloadPortal}
    />
  ) : null;

  const payCard = accepted && owing && paymentDetails ? (
    <section className="quote-pay-card" id="quote-pay" aria-labelledby="quote-pay-title">
      <h2 id="quote-pay-title">{secured ? 'Pay your balance' : 'Secure your date'}</h2>
      {glance}
      {accountSentence ? <p className="quote-availability">{accountSentence}</p> : null}
      {paymentDetails.ready ? (
        <PaymentChoices
          details={paymentDetails}
          depositPercent={snapshot.depositPercent}
          otherAmount={otherAmount}
          busy={busy}
          onOtherAmount={setOtherAmount}
          onPay={(type, amountMinor) => startPayment(type, amountMinor)}
        />
      ) : (
        <p className="quote-availability">PayFast is not connected yet. The amounts above are the rand figures that will be charged once payment is available.</p>
      )}
    </section>
  ) : null;

  const closedNote = expired
    ? 'This quotation has expired. Write to Carmen if you would still like to book.'
    : quote.status === 'declined'
      ? 'This quotation has been declined.'
      : quote.status === 'superseded'
        ? 'This quotation has been replaced by a newer version. Carmen will send you the new link.'
        : quote.status === 'cancelled' ? 'This quotation has been cancelled.' : '';

  const dueMinor = figures?.currentlyDueMinor || paymentDetails?.depositDueMinor || paymentDetails?.outstandingMinor || 0;
  const totalText = formatQuoteMoney(snapshot.quoteTotalMinor, snapshot.currency);

  const shortDate = (value?: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return '';
    return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', timeZone: 'UTC' })
      .format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))));
  };

  const tabs: Array<{ id: ClientTab; label: string; short: string; hint: string; fresh?: boolean }> = [
    {
      id: 'quote',
      label: confirmedBooking ? 'Booking confirmation' : accepted ? 'Quote & payments' : 'Quotation',
      short: confirmedBooking ? 'Booking' : accepted ? 'Payments' : 'Quote',
      hint: closed
        ? 'Closed'
        : owing ? `${formatQuoteMoney(dueMinor, 'R')} due`
          : paidInFull ? 'Paid in full'
            : totalText,
    },
    {
      id: 'shoot',
      label: 'Date & location',
      short: 'Location',
      hint: confirmedBooking?.shootDate || bookedDate
        ? `${shortDate(confirmedBooking?.shootDate || bookedDate)} · ${confirmedBooking ? 'confirmed' : 'to confirm'}`
        : 'Date to agree',
    },
    {
      id: 'messages',
      label: 'Messages',
      short: 'Messages',
      hint: studioWaiting ? 'New from Carmen' : spokenCount > 0 ? `${spokenCount} message${spokenCount === 1 ? '' : 's'}` : 'Ask Carmen anything',
      fresh: studioWaiting,
    },
  ];

  const onTabKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((item) => item.id === activeTab);
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    goTo(tabs[next].id);
    document.getElementById(`quote-tab-${tabs[next].id}`)?.focus();
  };

  const shootPanel = confirmedBooking ? (
    <div className="quote-facts" data-testid="confirmed-shoot">
      <p className="quote-step-lead">Confirmation of your booking.</p>
      <dl>
        {scheduleRows() || (
          <>
            <div data-testid="preferred-date">
              <dt>Date</dt>
              <dd>
                {bookedDate ? formatPreferredDate(bookedDate) : 'To be agreed'}
                {bookedTime ? `, ${bookedTime}` : ''}
                {bookedEnd ? ` until ${bookedEnd}` : ''}
              </dd>
            </div>
            {durationFact()}
            <div data-testid="preferred-place">
              <dt>Place</dt>
              <dd>
                {bookedPlace || 'To be agreed'}
                {bookedMap ? <> · <a href={bookedMap} target="_blank" rel="noreferrer">Open in Google Maps</a></> : null}
              </dd>
            </div>
          </>
        )}
      </dl>
      {bookedMap ? <ShootMap mapsUrl={bookedMap} label={bookedPlace} /> : null}
      {confirmedBooking.calendarUrl ? <p><a href={confirmedBooking.calendarUrl} target="_blank" rel="noreferrer">Add to your calendar</a></p> : null}
      {confirmedBooking.clientNotes ? <p>{confirmedBooking.clientNotes}</p> : null}
      {confirmedBooking.preparation
        ? Object.entries(confirmedBooking.preparation).filter(([, value]) => value).map(([key, value]) => <p key={key}>{value}</p>)
        : null}
      {!closed ? (
        <p className="quote-date-note">
          <button type="button" className="quote-text-button" onClick={() => goTo('messages')}>Need a change? Message Carmen</button>
        </p>
      ) : null}
    </div>
  ) : deciding ? (
    <div className="quote-decision" id="quote-decision">
      <p className="quote-step-lead">
        {multiDay
          ? 'Carmen has set out each day below. Change a date or place that does not suit you.'
          : hasShootLocation(quote.preferredLocation) || quote.preferredDate
            ? 'Carmen has suggested the details below. Change anything that does not suit you.'
            : 'Add a date and place if you have them in mind. You can leave them for later.'}
      </p>
      {coverage && !multiDay ? <p className="quote-step-note" data-testid="package-duration">Duration: {coverage}</p> : null}
      {multiDay ? (
        <div data-testid="multi-day-schedule">
          {daySessions.map((session, index) => (
            <React.Fragment key={session.id}>
              {index > 0 ? <hr className="quote-day-separator" /> : null}
              <section className="quote-day" id={`quote-day-${session.id}`}>
                <h3>{session.label}</h3>
                {session.durationMinutes ? (
                  <p className="quote-day-duration">Duration: {formatPackageDuration(session.durationMinutes)}</p>
                ) : null}
                <ShootChoices
                  subject={session.label}
                  date={session.date}
                  time={session.startTime}
                  location={session.location}
                  dateInPast={Boolean(session.date) && session.date < studioTodayParts().date}
                  timeHasPassed={shootTimeHasPassed(session.date, session.startTime)}
                  onDate={(value) => updateChosenSession(session.id, { date: value })}
                  onTime={(value) => updateChosenSession(session.id, { startTime: value })}
                  onLocation={(value) => updateChosenSession(session.id, { location: value })}
                />
              </section>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <ShootChoices
          date={chosenDate}
          time={chosenTime}
          location={chosenLocation}
          dateInPast={dateInPast}
          timeHasPassed={timeHasPassed}
          onDate={setChosenDate}
          onTime={setChosenTime}
          onLocation={setChosenLocation}
        />
      )}
      <p className="quote-step-note">Accepting confirms this quotation and the booking terms. Carmen confirms the date and place with you afterwards.</p>
      <div className="quote-step-actions">
        <button type="button" className="quote-accept" disabled={busy || shootChoiceInvalid} onClick={() => decide('accept')}>
          Accept quotation
        </button>
        <button type="button" className="quote-text-button" onClick={() => goTo('quote')}>Back to the quotation</button>
      </div>
      <div className="quote-decline-row">
        {confirmDecline ? (
          <>
            <span>Decline this quotation? Carmen will be told.</span>
            <button type="button" className="quote-text-button" disabled={busy} onClick={() => decide('decline')}>Yes, decline</button>
            <button type="button" className="quote-text-button" onClick={() => setConfirmDecline(false)}>Keep it</button>
          </>
        ) : (
          <button type="button" className="quote-text-button" onClick={() => setConfirmDecline(true)}>Not for you? Decline the quotation</button>
        )}
      </div>
    </div>
  ) : (
    <div className="quote-facts">
      <dl>
        {scheduleRows() || (
          <>
            <div data-testid="preferred-date">
              <dt>Date</dt>
              <dd>{shownDate ? formatPreferredDate(shownDate) : 'To be agreed'}{shownTime ? `, ${shownTime}` : ''}</dd>
            </div>
            {durationFact()}
            <div data-testid="preferred-place">
              <dt>Place</dt>
              <dd>
                {shownPlace || 'To be agreed'}
                {shownMap ? <> · <a href={shownMap} target="_blank" rel="noreferrer">Open in Google Maps</a></> : null}
              </dd>
            </div>
          </>
        )}
      </dl>
      {shownMap ? <ShootMap mapsUrl={shownMap} label={shownPlace} /> : null}
      {!closed ? (
        <p className="quote-date-note">
          Carmen will confirm the date and place with you.{' '}
          <button type="button" className="quote-text-button" onClick={() => goTo('messages')}>Need a change? Message Carmen</button>
        </p>
      ) : null}
    </div>
  );

  const shootSummary = !multiDay && (bookedDate || bookedPlace) ? (
    <section className="quote-shoot-summary" aria-labelledby="quote-shoot-summary-title">
      <h2 id="quote-shoot-summary-title">{confirmedBooking ? 'Booking confirmation' : 'Your shoot'}</h2>
      <div className="quote-facts">
        <dl>
          <div>
            <dt>Date</dt>
            <dd>
              {bookedDate ? formatPreferredDate(bookedDate) : 'To be agreed'}
              {bookedTime ? `, ${bookedTime}` : ''}
              {' · '}{confirmedBooking ? 'confirmed' : 'to confirm'}
            </dd>
          </div>
          {durationFact()}
          <div>
            <dt>Place</dt>
            <dd>{bookedPlace || 'To be agreed'}</dd>
          </div>
        </dl>
        {!closed ? (
          <button type="button" className="quote-text-button" onClick={() => goTo('shoot')}>
            {deciding ? 'Change date or place' : 'See date and location'}
          </button>
        ) : null}
      </div>
    </section>
  ) : null;

  const guides = quote.guides && quote.guides.length > 0 && token ? (
    <section className="quote-guides" aria-labelledby="quote-guides-title" data-testid="quote-guides">
      <h2 id="quote-guides-title">Guides</h2>
      <p className="quote-guides-lead">
        A few notes to help you prepare. Open any that are useful — nothing downloads until you choose one.
      </p>
      <ul>
        {quote.guides.map((guide) => (
          <li key={guide.id}>
            <a href={`/api/public/quotes/${token}/guides/${guide.id}`} target="_blank" rel="noopener noreferrer">
              {guide.title}
            </a>
          </li>
        ))}
      </ul>
    </section>
  ) : null;

  const quotePanel = (
    <>
      {experience}
      {shootSummary}
      {investment}
      {payCard}
      {accepted && !owing ? <div className="quote-account">{glance}</div> : null}
      {history ? (
        <section className="quote-payments" aria-labelledby="quote-payments-title">
          <h2 id="quote-payments-title">Payments</h2>
          {history}
        </section>
      ) : null}
      {currencyNote}
      {terms}
      {guides}
      {deciding ? (
        <div className="quote-step-actions">
          <button type="button" className="quote-accept" onClick={() => goTo('shoot')}>Next: date and location</button>
        </div>
      ) : null}
    </>
  );

  const ready = portalLoaded && activeTab !== null && !(accepted && !paymentDetails && !error);

  return (
    <main className="client-quote">
      <article className="quote-folio">
        {masthead}
        <section className="quote-intro is-compact">
          <h1>{quote.clientName || (bookingOpen ? 'Your booking' : 'Your quotation')}</h1>
          {bookingOpen ? <p className="quote-shoot">Booking confirmation</p> : null}
          {clientFacingShootType(quote.shootType) ? <p className="quote-shoot">{clientFacingShootType(quote.shootType)}</p> : null}
          {deciding && quote.personalMessage ? <QuoteLetter text={quote.personalMessage} /> : null}
        </section>
        {closedNote || returnMessage ? (
          <section className={`quote-next${closed ? ' is-closed' : ''}`} aria-label="Status" role="status">
            {returnMessage ? <p className="quote-next-return">{returnMessage}</p> : null}
            {closedNote ? <p className="quote-next-text">{closedNote}</p> : null}
          </section>
        ) : null}
        {error ? <p className="client-quote-status" role="alert">{error}</p> : null}
        {!ready ? (
          <p className="quote-state">{bookingOpen ? 'Opening your booking…' : 'Opening your quotation…'}</p>
        ) : (
          <>
            <span id="quote-tabs-anchor" className="quote-tabs-anchor" aria-hidden="true" />
            <div className="quote-tabs" role="tablist" aria-label={bookingOpen ? 'Your booking' : 'Your quotation'} onKeyDown={onTabKey}>
              {tabs.map((item) => (
                <button
                  key={item.id}
                  id={`quote-tab-${item.id}`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === item.id}
                  aria-controls={`quote-panel-${item.id}`}
                  tabIndex={activeTab === item.id ? 0 : -1}
                  className={`${activeTab === item.id ? 'is-active' : ''}${item.fresh ? ' is-fresh' : ''}`}
                  onClick={() => goTo(item.id)}
                >
                  <span className="quote-tab-label">
                    <span className="quote-tab-long">{item.label}</span>
                    <span className="quote-tab-short">{item.short}</span>
                  </span>
                  <span className="quote-tab-hint">{item.hint}</span>
                </button>
              ))}
            </div>
            {tabs.map((item) => (
              <section
                key={item.id}
                id={`quote-panel-${item.id}`}
                role="tabpanel"
                aria-labelledby={`quote-tab-${item.id}`}
                className="quote-panel"
                hidden={activeTab !== item.id}
              >
                {item.id === 'shoot' ? shootPanel : item.id === 'messages' ? chat : quotePanel}
              </section>
            ))}
          </>
        )}
      </article>
    </main>
  );
};

export default ClientQuotePage;
